import { businessInfo } from '../data/business';
import { Facebook, Instagram, MapPin, Phone, Mail, Zap, ArrowRight } from 'lucide-react';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary-footer text-white pt-16 sm:pt-20 pb-10 border-t-8 border-primary">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-12 mb-12 sm:mb-16">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div className="flex flex-col">
                <span className="font-heading font-extrabold text-xl sm:text-2xl leading-none tracking-tight">BUDIMLIJA</span>
                <span className="text-xs sm:text-sm font-bold tracking-[0.2em] text-red-300">ELEKTRO</span>
              </div>
            </div>
            <p className="text-red-100 mb-6 sm:mb-8 leading-relaxed font-medium text-base sm:text-lg">
              Profesionalne elektro usluge, semiranje ormara i instalacije jake i slabe struje. 
              Kvalitet, sigurnost i dugogodišnje iskustvo.
            </p>
            <div className="flex gap-4">
              <a 
                href={businessInfo.social.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/10 rounded flex items-center justify-center hover:bg-white hover:text-primary transition-all border border-white/20 active:scale-95"
              >
                <Facebook size={24} />
              </a>
              <a 
                href={businessInfo.social.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white/10 rounded flex items-center justify-center hover:bg-white hover:text-primary transition-all border border-white/20 active:scale-95"
              >
                <Instagram size={24} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg sm:text-xl font-bold mb-6 sm:mb-8 border-b-2 border-red-700 pb-4 inline-block text-white">Brzi Linkovi</h4>
            <ul className="space-y-4">
              <li><a href="#home" className="text-red-100 hover:text-white hover:pl-2 transition-all flex items-center gap-2 font-medium py-1"><ArrowRight size={16} className="text-red-400" /> Početna</a></li>
              <li><a href="#about" className="text-red-100 hover:text-white hover:pl-2 transition-all flex items-center gap-2 font-medium py-1"><ArrowRight size={16} className="text-red-400" /> O Nama</a></li>
              <li><a href="#services" className="text-red-100 hover:text-white hover:pl-2 transition-all flex items-center gap-2 font-medium py-1"><ArrowRight size={16} className="text-red-400" /> Usluge</a></li>
              <li><a href="#gallery" className="text-red-100 hover:text-white hover:pl-2 transition-all flex items-center gap-2 font-medium py-1"><ArrowRight size={16} className="text-red-400" /> Galerija</a></li>
              <li><a href="#contact" className="text-red-100 hover:text-white hover:pl-2 transition-all flex items-center gap-2 font-medium py-1"><ArrowRight size={16} className="text-red-400" /> Kontakt</a></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg sm:text-xl font-bold mb-6 sm:mb-8 border-b-2 border-red-700 pb-4 inline-block text-white">Usluge</h4>
            <ul className="space-y-4">
              <li className="text-red-100 font-medium flex items-center gap-2 py-1"><Zap size={16} className="text-red-400 shrink-0" /> Semiranje elektro ormara</li>
              <li className="text-red-100 font-medium flex items-center gap-2 py-1"><Zap size={16} className="text-red-400 shrink-0" /> Industrijske instalacije</li>
              <li className="text-red-100 font-medium flex items-center gap-2 py-1"><Zap size={16} className="text-red-400 shrink-0" /> Jake i slabe struje</li>
              <li className="text-red-100 font-medium flex items-center gap-2 py-1"><Zap size={16} className="text-red-400 shrink-0" /> Elektro instalacije</li>
              <li className="text-red-100 font-medium flex items-center gap-2 py-1"><Zap size={16} className="text-red-400 shrink-0" /> Održavanje sistema</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg sm:text-xl font-bold mb-6 sm:mb-8 border-b-2 border-red-700 pb-4 inline-block text-white">Kontakt Info</h4>
            <ul className="space-y-6">
              <li>
                <a href={`tel:${businessInfo.phoneClean}`} className="flex items-start gap-4 text-white hover:text-red-200 transition-colors group">
                  <div className="w-10 h-10 bg-white/10 rounded flex items-center justify-center shrink-0 group-hover:bg-white group-hover:text-primary transition-colors">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-red-300 uppercase font-bold tracking-wider mb-1">Telefon</p>
                    <span className="font-bold text-lg sm:text-xl">{businessInfo.phone}</span>
                  </div>
                </a>
              </li>
              <li>
                <a href={`mailto:${businessInfo.email}`} className="flex items-start gap-4 text-white hover:text-red-200 transition-colors group">
                  <div className="w-10 h-10 bg-white/10 rounded flex items-center justify-center shrink-0 group-hover:bg-white group-hover:text-primary transition-colors">
                    <Mail size={20} />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-xs text-red-300 uppercase font-bold tracking-wider mb-1">Email</p>
                    <span className="font-medium break-all text-sm sm:text-base">{businessInfo.email}</span>
                  </div>
                </a>
              </li>
              <li>
                <a href={businessInfo.mapsLink} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 text-white hover:text-red-200 transition-colors group">
                  <div className="w-10 h-10 bg-white/10 rounded flex items-center justify-center shrink-0 group-hover:bg-white group-hover:text-primary transition-colors">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-red-300 uppercase font-bold tracking-wider mb-1">Lokacija</p>
                    <span className="font-medium text-sm sm:text-base">{businessInfo.address}<br/>{businessInfo.location}</span>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-red-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-red-300 font-medium text-center md:text-left">
          <p>&copy; {currentYear} Budimlija Elektro. Sva prava zadržana.</p>
        </div>
      </div>
    </footer>
  );
}
